import React, { memo, useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory, Redirect } from 'react-router'
import Button from '@material-ui/core/Button'
import { useApolloClient, useMutation } from '@apollo/react-hooks'
import { FetchResult } from 'apollo-link'
import { toast } from 'react-toastify'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import mixpanel from 'mixpanel-browser'

import styles from './styles'
import { EAppStateActionTypes, TProfileSettingsProps } from 'models'
import {
	CheckUsername,
	CheckEmail,
	UpdateUsername,
	UpdateUserSettings,
	StoreValue,
	SendUpdateEmail,
	DeleteAccountMutation,
	LogoutMutation,
} from 'store'

const useStyles = makeStyles(styles)

const ProfileSettings: React.FunctionComponent<TProfileSettingsProps> = ({
	canEdit = false,
	updateUser,
	storeUserUid,
	storeUserIg,
	storeUser,
	firebase: { instance: firebaseApp },
	storeDispatch,
}) => {
	let [toggleDeleteAccountModal, setToggleDeleteAccountModal] =
		useState<boolean>(false)
	const history = useHistory()
	const theme: Theme = useTheme()
	const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
	const [updateUserSettings] = useMutation(UpdateUserSettings)
	const [deleteAccountMutationHandle] = useMutation(DeleteAccountMutation)
	const [logoutMutationHandle] = useMutation(LogoutMutation)
	const { personID }: { personID?: string } = useParams()
	const uid = personID || null
	const client = useApolloClient()
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const [firstName, setFirstName] = useState(
		storeUser?.profile?.firstName || '',
	)
	const updateFirstName = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setFirstName(e.target.value)
		},
		[],
	)
	const [lastName, setLastName] = useState(storeUser?.profile?.lastName || '')
	const updateLastName = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setLastName(e.target.value)
		},
		[],
	)
	const [checkingUsername, setCheckingUsername] = useState(false)
	const [unavailable, setUnavailable] = useState(false)
	const [available, setAvailable] = useState(false)
	const [username, setUsername] = useState((storeUser && storeUser.uid) || '')
	const updateUsername = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setUnavailable(false)
			setAvailable(false)
			setUsername(e.target.value)
		},
		[],
	)

	const checkUsername = useCallback(async () => {
		setCheckingUsername(true)
		const {
			data: { checkUsername },
		} = await client.query({
			query: CheckUsername,
			variables: {
				uid: username,
			},
			fetchPolicy: 'network-only',
		})
		if (checkUsername && checkUsername.status) {
			setUnavailable(true)
			setTimeout(() => {
				setUnavailable(false)
				setCheckingUsername(false)
			}, 2500)
		} else {
			setAvailable(true)
			setCheckingUsername(false)
		}
	}, [username, client])
	const saveUsername = useCallback(async () => {
		if (!available) return false
		const {
			data: { updateUsername },
		}: FetchResult<any> = await client.mutate({
			mutation: UpdateUsername,
			variables: {
				userId:
					(storeUser &&
						storeUserIg &&
						storeUserUid !== uid &&
						storeUser.id) ||
					undefined,
				username,
			},
		})
		if (updateUsername.status) {
			history.replace(`/${i18n.language}/people/${username}`)
			updateUser()
		} else {
			toast.error(t('Sorry ... something went wrong'), {
				position: 'top-center',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			})
		}
	}, [
		available,
		client,
		uid,
		storeUser,
		username,
		history,
		i18n.language,
		updateUser,
		t,
		storeUserIg,
		storeUserUid,
	])

	const [checkingEmail, setCheckingEmail] = useState(false)
	const [emailUnavailable, setEmailUnavailable] = useState(false)
	const [emailAvailable, setEmailAvailable] = useState(false)
	const [verificationEmailSent, setVerificationEmailSent] = useState(false)
	const [email, setEmail] = useState(
		(storeUser && storeUser.profile.email) || '',
	)

	const storedEmail = storeUser?.profile.email

	const updateEmail = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setEmailUnavailable(false)
			setEmailAvailable(false)
			setVerificationEmailSent(false)
			setEmail(e.target.value)
		},
		[],
	)

	const checkEmail = useCallback(async () => {
		setCheckingEmail(true)
		const {
			data: { checkEmail },
		} = await client.query({
			query: CheckEmail,
			variables: {
				email,
			},
			fetchPolicy: 'network-only',
		})
		if (checkEmail && checkEmail.status) {
			setEmailUnavailable(true)
			setTimeout(() => {
				setEmailUnavailable(false)
				setCheckingEmail(false)
			}, 2500)
		} else {
			setEmailAvailable(true)
			setCheckingEmail(false)
		}
	}, [email, client])

	const saveEmail = useCallback(async () => {
		if (!emailAvailable) return false
		await client
			.mutate({
				mutation: SendUpdateEmail,
				variables: {
					language: 'en',
					newEmail: email,
				},
			})

			.then(() => {
				setEmailAvailable(false)
				setVerificationEmailSent(true)
				setTimeout(() => {
					setVerificationEmailSent(false)
				}, 5000)
			})
			.catch(() => {})
	}, [emailAvailable, client, email])

	const [currentPassword, setCurrentPassword] = useState('')
	const checkCurrentPassword = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setCurrentPassword(e.target.value)
		},
		[],
	)

	const [newPassword, setNewPassword] = useState('')
	const updateNewPassword = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setNewPassword(e.target.value)
		},
		[],
	)
	const [confirmNewPassword, setConfirmNewPassword] = useState('')
	const updateConfirmNewPassword = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setConfirmNewPassword(e.target.value)
		},
		[],
	)
	const [canUpdate, setCanUpdate] = useState(false)

	const logout = async () => {
		await logoutMutationHandle()
		localStorage.clear()
		mixpanel.reset()
		await firebaseApp
			.auth()
			.signInAnonymously()
			.then((firebaseUser: any) => {
				storeDispatch({
					type: EAppStateActionTypes.resetUser,
					firebaseUser,
				})
			})
			.catch((err: any) => {
				console.error(err)
			})
	}

	const deleteAccount = async () => {
		try {
			if (!storeUser?.id) {
				throw new Error('Error! Something went wrong!')
			}
			await deleteAccountMutationHandle({
				variables: { userId: storeUser.id },
			})

			toast.success(t('Account deleted successfully!'), {
				position: 'top-center',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			})
			await logout()
			history.push({ pathname: `/` })
		} catch (error) {
			toast.error(t('Sorry ... something went wrong'), {
				position: 'top-center',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			})
			console.error(error)
		}
	}

	const update = useCallback(async () => {
		if (!canUpdate) return false

		let payload = {
			userId:
				(storeUser &&
					storeUserIg &&
					storeUserUid !== uid &&
					storeUser.id) ||
				undefined,
			firstName,
			lastName,
			// password,
			currentPassword,
			newPassword,
			confirmNewPassword,
		}
		try {
			const { data }: any = await updateUserSettings({
				variables: { userSettings: payload },
			})
			// const {
			// 	data: { updateUserSettings },
			// }: FetchResult<any> = await client.mutate({
			// 	mutation: UpdateUserSettings,
			// 	variables: {
			// 		userSettings: payload,
			// 	},
			// })
			const currentPasswordVerification =
				data.updateUserSettings.currentPasswordCheck

			if (currentPasswordVerification === null) {
				updateUser()
				toast.success(t('Yey ... you are all set'), {
					position: 'top-center',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
				})
			} else if (currentPasswordVerification === false) {
				toast.error(
					t('Your current password is missing or incorrect'),
					{
						position: 'top-center',
						autoClose: 5000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
					},
				)
			}
		} catch (e) {
			toast.error(t('Sorry ... something went wrong'), {
				position: 'top-center',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
			})
		}
	}, [
		firstName,
		lastName,
		storeUserIg,
		storeUserUid,
		currentPassword,
		newPassword,
		confirmNewPassword,
		storeUser,
		uid,
		canUpdate,
		updateUser,
		t,
		updateUserSettings,
	])

	useEffect(() => {
		let currentCanUpdate = false
		if (
			firstName &&
			storeUser &&
			storeUser.profile &&
			storeUser.profile.firstName !== firstName
		)
			currentCanUpdate = true
		if (
			lastName &&
			storeUser &&
			storeUser.profile &&
			storeUser.profile.lastName !== lastName
		)
			currentCanUpdate = true
		if (newPassword && newPassword === confirmNewPassword)
			currentCanUpdate = true
		// if (currentCanUpdate && !password) currentCanUpdate = false
		setCanUpdate(currentCanUpdate)
	}, [
		firstName,
		lastName,
		newPassword,
		confirmNewPassword,
		// password,
		storeUser,
	])

	useEffect(() => {
		setFirstName(
			(storeUser && storeUser.profile && storeUser.profile.firstName) ||
				'',
		)
		setLastName(
			(storeUser && storeUser.profile && storeUser.profile.lastName) ||
				'',
		)
		setUsername((storeUser && storeUser.uid) || '')
		setEmail((storeUser && storeUser.profile.email) || '')
	}, [storeUser, uid])

	if (!canEdit || !uid) return <Redirect to={`/${i18n.language}`} />
	return (
		<>
			<Grid
				className={classes.profileMainContent}
				container={true}
				direction="column"
			>
				<Grid item={true} className={classes.sectionContent}>
					<Grid container={true} spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								label={
									!firstName
										? t('First Name is required')
										: t('First Name')
								}
								error={!firstName}
								className={classes.textField}
								value={firstName}
								onChange={updateFirstName}
								margin="normal"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label={
									!lastName
										? t('Last Name is required')
										: t('Last Name')
								}
								error={!lastName}
								className={classes.textField}
								value={lastName}
								onChange={updateLastName}
								margin="normal"
							/>
						</Grid>
						<Grid item xs={12}>
							<Grid container={true} spacing={2}>
								<Grid item xs={8} sm={6}>
									<TextField
										label={
											!username
												? t('Username is required')
												: t('Username')
										}
										error={!username}
										className={classes.textField}
										value={username}
										onChange={updateUsername}
										margin="normal"
										helperText={t(
											'letters, numbers, including .-_',
										)}
									/>
								</Grid>
								<Grid
									item
									xs={4}
									sm={6}
									style={{
										display: 'flex',
										flexDirection: 'row',
									}}
								>
									<Button
										disabled={
											username === uid ||
											checkingUsername ||
											!username
										}
										style={{ alignSelf: 'center' }}
										variant="outlined"
										size="small"
										color="secondary"
										onClick={
											available
												? saveUsername
												: checkUsername
										}
									>
										{available
											? t('Save current username')
											: unavailable
											? 'Username taken'
											: t('Check Availability')}
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Grid container={true} spacing={2}>
								<Grid item xs={8} sm={6}>
									<TextField
										label={
											!email
												? t('Email address is required')
												: t('Email Address')
										}
										error={!email}
										className={classes.textField}
										value={email}
										onChange={updateEmail}
										margin="normal"
										helperText={t(
											'enter a valid email address',
										)}
									/>
								</Grid>
								<Grid
									item
									xs={4}
									sm={6}
									style={{
										display: 'flex',
										flexDirection: 'row',
									}}
								>
									<Button
										disabled={
											checkingEmail ||
											!email ||
											verificationEmailSent ||
											storedEmail === email
										}
										style={{ alignSelf: 'center' }}
										variant="outlined"
										size="small"
										color="secondary"
										onClick={
											emailAvailable
												? saveEmail
												: checkEmail
										}
									>
										{verificationEmailSent
											? t('Check your inbox email')
											: emailAvailable
											? t('Save current email address')
											: emailUnavailable
											? 'Email address already in use'
											: t('Check Availability')}
									</Button>
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid container={true} spacing={2}>
								<Grid item xs={12} sm={4}>
									<TextField
										label={t('Current Password')}
										className={classes.textField}
										type="password"
										value={currentPassword}
										onChange={checkCurrentPassword}
										margin="normal"
										helperText={t('enter current password')}
									/>
								</Grid>

								<Grid item xs={12} sm={4}>
									<TextField
										disabled={!currentPassword}
										label={t('New Password')}
										className={classes.textField}
										type="password"
										value={newPassword}
										onChange={updateNewPassword}
										margin="normal"
										helperText={t(
											'only fill in to change the password',
										)}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={4}
									style={{
										display: 'flex',
										flexDirection: 'row',
									}}
								>
									<TextField
										disabled={!currentPassword}
										label={
											newPassword !== confirmNewPassword
												? t('Passwords do not match')
												: t('Confirm New Password')
										}
										error={
											newPassword !== confirmNewPassword
										}
										className={classes.textField}
										type="password"
										value={confirmNewPassword}
										onChange={updateConfirmNewPassword}
										margin="normal"
										helperText={t(
											'must match the new password',
										)}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid
							item
							xs={12}
							style={{ display: 'flex', flexDirection: 'row' }}
						>
							<Button
								style={{
									textTransform: 'capitalize',
									position: 'absolute',
								}}
								variant="text"
								size="medium"
								color="secondary"
								onClick={() =>
									setToggleDeleteAccountModal(true)
								}
							>
								{t('Delete Account')}
							</Button>
							<Button
								disabled={!canUpdate}
								style={{ margin: 'auto' }}
								variant="outlined"
								size="medium"
								color="secondary"
								onClick={update}
							>
								{t('Update')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Dialog
				open={toggleDeleteAccountModal}
				onClose={() => setToggleDeleteAccountModal(false)}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle id="responsive-dialog-title">
					{t('Are you sure you want to delete the account?')}
				</DialogTitle>
				<DialogActions>
					<Button
						color="primary"
						autoFocus
						onClick={() => setToggleDeleteAccountModal(false)}
					>
						{t('No')}
					</Button>
					<Button color="secondary" onClick={deleteAccount}>
						{t('Yes')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default memo((props: TProfileSettingsProps) => (
	<StoreValue
		keys={[
			'user._ig|storeUserIg',
			'user.uid|storeUserUid',
			'user|storeUser',
			'firebase',
			'storeDispatch',
		]}
	>
		<ProfileSettings {...props} />
	</StoreValue>
))
